import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';

// Pages
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import MyExpenses from './pages/MyExpenses';
import NewExpense from './pages/NewExpense';
import ExpenseDetail from './pages/ExpenseDetail';
import AdminDashboard from './pages/AdminDashboard';
import AdminExpenses from './pages/AdminExpenses';
import AdminEmployees from './pages/AdminEmployees';
import ExpensesSheet from './pages/ExpensesSheet';
import EmployeeExpenseDashboard from './pages/EmployeeExpenseDashboard';
import EmployeeExpenseForm from './pages/EmployeeExpenseForm';
import MySubmissions from './pages/MySubmissions';
import ProfileSettings from './pages/ProfileSettings';
import AdminExpenseSheet from './pages/AdminExpenseSheet';
import EmployeeWiseSheet from './pages/EmployeeWiseSheet';
import PaymentEntry from './pages/PaymentEntry';

// Components
import Navbar from './components/Navbar';
import Loading from './components/Loading';

const ProtectedRoute = ({ children, adminOnly = false, subAdminAllowed = false }) => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;

  if (adminOnly) {
    const isAdmin = user.role === 'admin';
    const isSubAdmin = user.subAdmin === true;
    if (!isAdmin && !(subAdminAllowed && isSubAdmin)) {
      return <Navigate to={isAdmin ? '/admin' : '/employee-dashboard'} replace />;
    }
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) return <Loading />;
  if (user) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/employee-dashboard'} replace />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <div className="app-container">
      {user && <Navbar />}
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

        {/* Employee Routes */}
        <Route path="/employee-dashboard" element={<ProtectedRoute><EmployeeExpenseDashboard /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/expenses" element={<ProtectedRoute><MyExpenses /></ProtectedRoute>} />
        <Route path="/expenses/new" element={<ProtectedRoute><NewExpense /></ProtectedRoute>} />
        <Route path="/expenses/:id" element={<ProtectedRoute><ExpenseDetail /></ProtectedRoute>} />
        <Route path="/expenses-sheet" element={<ProtectedRoute><ExpensesSheet /></ProtectedRoute>} />
        <Route path="/submit-expense" element={<ProtectedRoute><EmployeeExpenseForm /></ProtectedRoute>} />
        <Route path="/my-submissions" element={<ProtectedRoute><MySubmissions /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />

        {/* Admin Routes */}
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/expenses" element={<ProtectedRoute adminOnly><AdminExpenses /></ProtectedRoute>} />
        <Route path="/admin/employees" element={<ProtectedRoute adminOnly><AdminEmployees /></ProtectedRoute>} />
        <Route path="/admin/expense-sheet" element={<ProtectedRoute adminOnly><AdminExpenseSheet /></ProtectedRoute>} />
        <Route path="/admin/employee-sheets" element={<ProtectedRoute adminOnly subAdminAllowed><EmployeeWiseSheet /></ProtectedRoute>} />
        <Route path="/admin/payment-entry" element={<ProtectedRoute adminOnly subAdminAllowed><PaymentEntry /></ProtectedRoute>} />

        <Route path="/" element={<Navigate to={user ? (user.role === 'admin' ? '/admin' : '/employee-dashboard') : '/login'} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;